import React, { useState } from 'react';
import { sendPlatformInvite } from './api';

/**
 * Public sign-up is closed; this is how a new customer gets in. The server
 * creates the account and emails a one-click link that lands them in
 * onboarding to set up their own workspace.
 */
export default function Invite({ toast }: { toast: (m: string) => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<string[]>([]);

  const send = async () => {
    const value = email.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const { existingAccount } = await sendPlatformInvite(value, name);
      toast(
        existingAccount
          ? `${value} already has an account — sent them a sign-in link`
          : `Invitation sent to ${value}`,
      );
      setSent((prev) => [`${value}${existingAccount ? ' (existing account, sign-in link)' : ''}`, ...prev]);
      setEmail('');
      setName('');
    } catch (e) {
      toast(`Could not send: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 720 }}>
      <section className="card">
        <h2 style={{ marginTop: 0 }}>Invite someone to AssetWRX</h2>
        <p className="sub" style={{ marginBottom: 10 }}>
          Creates their account and emails a one-click link. It signs them in and takes them to set up their own
          workspace, with no password and no sign-up form. The link lasts one hour; if it expires, invite them
          again. To add someone to an existing workspace instead, the workspace owner invites them from Team.
        </p>
        <div className="row" style={{ alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 220 }}>
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="person@company.com"
              onKeyDown={(e) => e.key === 'Enter' && void send()}
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label>Name (optional)</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alex Morgan"
              onKeyDown={(e) => e.key === 'Enter' && void send()}
            />
          </div>
          <button className="btn primary" disabled={busy || !email.trim()} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </section>
      {sent.length ? (
        <section className="card">
          <h2 style={{ marginTop: 0 }}>Sent this session</h2>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {sent.map((s, i) => (
              <li key={`${s}-${i}`}>{s}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
